import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import { Button } from 'luna-react';
import Panel from '../components/Panel';
import ViewportManager, { ViewportSize } from '../utils/ViewportManager';
import { filtersSerializer, filtersParser, groupFilterOptions, createStoredFilters } from '../utils/FiltersFormatter';
import { createUrlGenerator } from '../utils/UrlGenerator';
import { filterTable, extractFilters } from '../utils/FiltersService';
import { trackEvent, trackFiltersChangeEvent } from '../utils/TrackEventManager';
import { Subject } from 'rxjs/Subject';

const defaultState = {
  filters: [],
  isVisible: false,
  storedFilters: [],
};

// FIXME: ⚡️ couldn't find a workaround other than jQuery to handle:
// - sorting
// - tooltips
// - tracking

export class TableFilters extends PureComponent {
  constructor(props, context) {
    super(props, context);

    const {
      wrapperNode,
      template,
      site,
      categoryId,
      fieldset,
      origin,
      sort,
      throwOnEmptyFilters,
      baseUrl,
      filters,
      defaultTableFilter,
      pagination,
    } = props;

    const urlGenerator = createUrlGenerator({
      serializeFn: filtersSerializer,
      template,
      site,
      categoryId,
      fieldset,
      origin,
      sort,
      throwOnEmptyFilters,
      baseUrl,
      defaultTableFilter,
      pagination,
    });

    const tableNode = wrapperNode.querySelector('table');
    const tableBodyNode = tableNode.querySelector('tbody');
    const itemsTotal = tableBodyNode.children.length;
    const currentItemsTotal = itemsTotal;
    const groupedFilters = filters.map(filterElement => ({
      ...filterElement,
      options: groupFilterOptions(filterElement.options),
    }));

    this.state = Object.assign({}, defaultState, {
      filters: groupedFilters,
      urlGenerator,
      tableNode,
      tableBodyNode,
      currentItemsTotal,
      itemsTotal,
    });

    this.onSearch$ = new Subject();
  }

  onPanelChange = (deviceViewport) => ({ target }) => {
    if (!this.props.autoApply) {
      return;
    }

    const isViewportSmall = deviceViewport === ViewportSize.Small;

    if (!isViewportSmall) {
      this.submitForm(target.form);
    }
  };

  onPanelApply = (deviceViewport) => (ev) => {
    const { target: form } = ev;
    const isViewportSmall = deviceViewport === ViewportSize.Small;

    this.submitForm(form);

    if (isViewportSmall) {
      this.toggleVisibility();
    }
  };

  onPanelReset = (deviceViewport) => (ev) => {
    this.submitForm(null);

    const isViewportSmall = deviceViewport === ViewportSize.Small;
    const shouldToggleTheForm = this.state.isVisible && isViewportSmall;

    if (shouldToggleTheForm) {
      this.toggleVisibility();
    }
  };

  onPanelCancel = ({ target }) => {
    this.toggleVisibility();
  };

  submitForm = (form) => {
    let filters = [];

    if (form != null) {
      const formData = extractFilters(form);
      filters = filtersParser(formData);
      trackFiltersChangeEvent(filters);
    } else {
      trackEvent({ action: 'Reset' });
    }
    this.setState({ storedFilters: createStoredFilters(filters, this.props.defaultTableFilter) });
    this.onChange(filters);
  }

  setPanelVisibility(deviceViewport) {
    const { leftSidePosition, keepVisible } = this.props;
    const isDesktopView = deviceViewport === ViewportSize.Large ||
                          deviceViewport === ViewportSize.XLarge;

    return leftSidePosition && isDesktopView ? true : keepVisible;
  }

  updateItemNumbers = ({ currentItemsTotal, itemsTotal }) =>
    this.setState({
      currentItemsTotal,
      itemsTotal,
    });

  toggleVisibility = () => {
    this.setState((prevState) => {
      const isVisible = !prevState.isVisible;
      const action = isVisible ? 'Open' : 'Hide';

      trackEvent({ action });

      return { isVisible };
    });
  }

  componentDidMount() {
    const filters = [...this.state.filters];
    const { tableFiltersNode } = this.props;
    tableFiltersNode.removeAttribute('style');

    this.subscription = this.onSearch$
      .debounceTime(500)
      .subscribe(this.onFilter);

    if (parseInt(this.props.pagination, 10) !== 0) {
      const total = 
        parseInt(document.querySelector('.js-comparison-calculator').dataset.filteredCount, 10);
      this.setState({ filters, itemsTotal: total });
    } else {
      this.setState({ filters });
    }

    document.addEventListener('updateFilterCounter', (event) => {
      this.updateProductCount(event.params);
    });
  }

  componentWillUnmount() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  updateProductCount = ({ currentItemsTotal, itemsTotal }) => {
    const updatedCount = currentItemsTotal >= itemsTotal ? itemsTotal : currentItemsTotal;
    this.setState({ currentItemsTotal: updatedCount });
  }

  onChange(filters = []) {
    const { urlGenerator } = this.state;
    let url;

    try {
      url = urlGenerator({ filters });
    } catch (ex) {
      url = null;
    } finally {
      this.onSearch$.next(url)
    }
  }

  

  onFilter = (url) => {
    const { tableBodyNode, itemsTotal, storedFilters } = this.state;
    const { noResultsTemplate, pagination } = this.props;
    filterTable({
      updateItemNumbers: this.updateItemNumbers,
      tableBodyNode,
      url,
      noResultsTemplate,
      itemsTotal,
      storedFilters,
      pagination,
    });
  }

  onTrackChange(label) {
    trackEvent({ action: 'Change', label });
  }

  renderFilters(deviceViewport) {
    const { filters, isVisible, currentItemsTotal, itemsTotal } = this.state;
    const { keepVisible, autoApply } = this.props;
    const itemsCount = { current: currentItemsTotal, total: itemsTotal }
    const toggleButtonLabel = 'Refine';
    const toggleButtonClassNames = [
      'luna-button--primary',
      'luna-button--ghost',
    ];

    if (keepVisible) {
      toggleButtonClassNames.push('is-hidden@m');
    }

    return (<Panel
      autoApply={autoApply}
      filters={filters}
      isVisible={isVisible}
      keepVisible={this.setPanelVisibility(deviceViewport)}
      onCancel={this.onPanelCancel}
      onChange={this.onPanelChange(deviceViewport)}
      onReset={this.onPanelReset(deviceViewport)}
      onApply={this.onPanelApply(deviceViewport)}
      trackChange={this.onTrackChange}
      itemsCount={itemsCount}
    >
      <Button
        label={toggleButtonLabel}
        className={toggleButtonClassNames.join(' ')}
        onClick={this.toggleVisibility}
      />
    </Panel>);
  }

  render() {
    const { tableNode } = this.state;
    const { leftSidePosition } = this.props;
    const tableRoot = tableNode.parentElement.parentElement.parentElement;

    return (
      <ViewportManager>
        {({ deviceViewport }) => {
          if (leftSidePosition && (
            deviceViewport === ViewportSize.Large ||
            deviceViewport === ViewportSize.XLarge
          )){
            return ReactDOM.createPortal((
              <div className="table-filters" data-side-position="true">
                { this.renderFilters(deviceViewport) }
              </div>
              ), tableRoot
            );
          }
          return this.renderFilters(deviceViewport);
        }}
      </ViewportManager>
    );
  }
}

export default TableFilters;



// WEBPACK FOOTER //
// ./src/containers/TableFilters.js